// TypeScript port of the HTML tool's shared Three.js viewer (TC3DViewer), kept for the PDF report images only: the
// interactive 3D view is the react-three-fiber scene in 3d/. A WebGL canvas with an SVG overlay for the dimension
// lines/labels, plus the camera helpers the report captures use (preset views, framing of the screws, of the whole
// group or of a rectangle, near-orthographic projection). No render loop: the capture redraws after moving the camera.
// Imperative: the caller owns `container` and calls dispose() when done.
//
// Written for three r128, run here on r185. To look the same:
// - colours: r128 had no colour management (hex values used as-is in the lighting maths, no output conversion). The
//   renderer here writes linear output and every colour is set through linearColor() (no sRGB decoding), which
//   reproduces that pipeline without touching the global THREE.ColorManagement shared with the other 3D views.
//   The grain canvas texture keeps NoColorSpace (sampled as-is, like r128's LinearEncoding).
// - lights: the legacy (non physically-correct) lighting removed in r165 multiplied every light by PI, so the HTML
//   intensities are scaled by Math.PI.
// - MeshPhongMaterial's default specular is an sRGB colour since r152: set back to the r128 value.
import * as THREE from "three";

const SVG_NS = "http://www.w3.org/2000/svg";
const LEGACY_LIGHT_SCALE = Math.PI;
// The HTML viewer's lights (intensities scaled, colours used as-is). Shared with the react-three-fiber scene, so the
// interactive view and the report images light the elements the same way.
export const VIEWER_LIGHTS = {
  ambient: { color: 0xffffff, intensity: 0.75 * LEGACY_LIGHT_SCALE },
  main: { color: 0xffffff, intensity: 0.85 * LEGACY_LIGHT_SCALE, position: [100, 140, 100] },
  fill: { color: 0xdfe8ff, intensity: 0.35 * LEGACY_LIGHT_SCALE, position: [-80, 60, -60] },
} as const;
// the overlay is serialised into the report images: plain values, no CSS custom properties
const DIM_COLOR = "#3c3c3c";
const LABEL_COLOR = "#b99a5f";
const DIM_FONT = "monospace";

export type ViewName = "iso" | "front" | "top" | "side";

export type DimLineOptions = { light?: boolean; only?: string };
export type DimLineCommand = {
  kind: "line";
  p1: THREE.Vector3;
  p2: THREE.Vector3;
  offsetDir: THREE.Vector3;
  offsetAmt: number;
  label: string;
  opts?: DimLineOptions;
};
// force: the label of a force arrow, kept on the side `away` points to (beyond the arrow tip)
export type DimLabelCommand = {
  kind: "label";
  x: number;
  y: number;
  z: number;
  text: string;
  force?: { away: THREE.Vector3 };
};
export type DimCommand = DimLineCommand | DimLabelCommand;
export type DimFilter = (cmd: DimCommand) => boolean;

// Frame of the report's side view (set by the scene builder, read by the capture): centre and half sizes in scene units.
export type SideFrame = { cx: number; cy: number; cz: number; halfW: number; halfH: number };

type ScreenPoint = { x: number; y: number; behind: boolean };

export type WoodMaterialOptions = { vertical?: boolean; tintVariation?: boolean };

// Keys the viewer reads from Object3D.userData: `type` tags the product (the screws), framed by frameProductNow.
export type ViewerUserData = { type?: string };

export type TC3DViewer = {
  readonly container: HTMLElement;
  readonly overlay: SVGSVGElement;
  readonly renderer: THREE.WebGLRenderer;
  readonly group: THREE.Group;
  sideFrame: SideFrame | null;
  clearGroup(): void;
  refit(): void;
  resize(): void;
  redraw(): void;
  addDimLine(
    p1: THREE.Vector3,
    p2: THREE.Vector3,
    offsetDir: THREE.Vector3,
    offsetAmt: number,
    label: string,
    opts?: DimLineOptions,
  ): void;
  addLabel(x: number, y: number, z: number, text: string): void;
  setDimFilter(fn: DimFilter | null): void;
  setView(v: ViewName): void;
  // Pivot on the whole group's centre and zoom to fit it, instantly.
  frameGroupNow(marginFactor?: number): void;
  // Near-orthographic projection (FOV 2°) for the report side view: thicknesses dimensions match the timber edges.
  setOrtho(on: boolean): void;
  // Frame a rectangle (centre, half width/height in scene units) for the current view and FOV.
  frameRectNow(cx: number, cy: number, cz: number, halfW: number, halfH: number): void;
  // Like frameGroupNow on the product (children tagged userData.type) only, with a wider margin; whole group as fallback.
  frameProductNow(marginFactor?: number): void;
  woodMaterial(colorHex: number, opts?: WoodMaterialOptions): THREE.MeshPhongMaterial;
  dispose(): void;
};

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

// A colour whose hex value is used as-is (see the header comment).
export function linearColor(hex: number): THREE.Color {
  return new THREE.Color().setHex(hex, THREE.LinearSRGBColorSpace);
}

function materialsOf(o: THREE.Object3D): THREE.Material[] {
  const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
  if (!m) return [];
  return Array.isArray(m) ? m : [m];
}

// Frees the GPU resources of a subtree (geometries, materials; textures are shared and owned by the viewer).
export function disposeObjectTree(root: THREE.Object3D): void {
  root.traverse((o) => {
    const geometry = (o as THREE.Mesh).geometry as THREE.BufferGeometry | undefined;
    if (geometry) geometry.dispose();
    materialsOf(o).forEach((m) => m.dispose());
  });
}

function makeGrainTexture(): THREE.CanvasTexture {
  const cnv = document.createElement("canvas");
  cnv.width = 64;
  cnv.height = 64;
  const ctx = cnv.getContext("2d");
  if (ctx) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, 64, 64);
    for (let i = 0; i < 40; i++) {
      const gx = Math.random() * 64;
      const shade = 190 + Math.random() * 50;
      ctx.strokeStyle = `rgba(${shade | 0},${(shade * 0.8) | 0},${(shade * 0.52) | 0},${0.12 + Math.random() * 0.28})`;
      ctx.lineWidth = 0.5 + Math.random() * 1.3;
      ctx.beginPath();
      ctx.moveTo(gx, 0);
      ctx.lineTo(gx + (Math.random() - 0.5) * 6, 64);
      ctx.stroke();
    }
  }
  const tex = new THREE.CanvasTexture(cnv);
  tex.colorSpace = THREE.NoColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

// Wood materials with the procedural grain. A factory so both the imperative viewer (report captures) and the
// react-three-fiber scene build the same timber and can release the shared textures when done.
export type WoodMaterialFactory = {
  woodMaterial(colorHex: number, opts?: WoodMaterialOptions): THREE.MeshPhongMaterial;
  dispose(): void;
};

export function createWoodMaterialFactory(): WoodMaterialFactory {
  let grain: { vertical: THREE.CanvasTexture; horizontal: THREE.CanvasTexture } | null = null;
  function woodGrainTextures(): { vertical: THREE.CanvasTexture; horizontal: THREE.CanvasTexture } {
    if (!grain) {
      const vertical = makeGrainTexture();
      vertical.repeat.set(1, 3);
      const horizontal = vertical.clone();
      horizontal.center.set(0.5, 0.5);
      horizontal.rotation = Math.PI / 2;
      horizontal.needsUpdate = true;
      grain = { vertical, horizontal };
    }
    return grain;
  }
  return {
    woodMaterial(colorHex, opts = {}) {
      const g = woodGrainTextures();
      const tint = opts.tintVariation === false ? 1 : 0.9 + Math.random() * 0.2;
      return new THREE.MeshPhongMaterial({
        color: linearColor(colorHex).multiplyScalar(tint),
        map: opts.vertical === false ? g.horizontal : g.vertical,
        shininess: 8,
        specular: linearColor(0x111111),
      });
    },
    dispose() {
      grain?.vertical.dispose();
      grain?.horizontal.dispose();
      grain = null;
    },
  };
}

let viewerSeq = 0;

export function createTC3DViewer(container: HTMLElement): TC3DViewer {
  const markerId = `tcV3dArrow${++viewerSeq}`;
  container.innerHTML = "";

  const canvas = document.createElement("canvas");
  container.appendChild(canvas);

  const overlay = svgEl("svg", {});
  const defs = svgEl("defs", {});
  const marker = svgEl("marker", {
    id: markerId,
    viewBox: "0 0 10 10",
    refX: "8",
    refY: "5",
    markerWidth: "6",
    markerHeight: "6",
    orient: "auto-start-reverse",
  });
  marker.appendChild(svgEl("path", { d: "M0,1 L9,5 L0,9 Z", fill: DIM_COLOR }));
  defs.appendChild(marker);
  overlay.appendChild(defs);
  const drawGroup = svgEl("g", {});
  overlay.appendChild(drawGroup);
  container.appendChild(overlay);

  const scene = new THREE.Scene();
  // white background: more readable on paper, less ink
  scene.background = linearColor(0xffffff);
  const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 10000);
  // preserveDrawingBuffer: the report capture reads the canvas (toDataURL) some time after the last render
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.setPixelRatio(window.devicePixelRatio || 1);
  const ambLight = new THREE.AmbientLight(linearColor(VIEWER_LIGHTS.ambient.color), VIEWER_LIGHTS.ambient.intensity);
  scene.add(ambLight);
  const dirLight = new THREE.DirectionalLight(linearColor(VIEWER_LIGHTS.main.color), VIEWER_LIGHTS.main.intensity);
  dirLight.position.set(...VIEWER_LIGHTS.main.position);
  scene.add(dirLight);
  const dirLight2 = new THREE.DirectionalLight(linearColor(VIEWER_LIGHTS.fill.color), VIEWER_LIGHTS.fill.intensity);
  dirLight2.position.set(...VIEWER_LIGHTS.fill.position);
  scene.add(dirLight2);

  const group = new THREE.Group();
  scene.add(group);

  const woodMaterials = createWoodMaterialFactory();
  const woodMaterial = woodMaterials.woodMaterial;

  let disposed = false;
  let sizedW = 0;
  let sizedH = 0;
  function sizeToContainer(): void {
    if (disposed) return;
    const w = container.clientWidth;
    const h = container.clientHeight;
    // zero size (off screen between captures): nothing to resize
    if (!w || !h || (w === sizedW && h === sizedH)) return;
    sizedW = w;
    sizedH = h;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    // resizing clears the canvas: repaint now, a capture may read it before the next redraw
    redraw();
  }

  const halfFovRad = THREE.MathUtils.degToRad(camera.fov / 2);
  let baseDist = 10;
  let MIN_ZOOM = 0.1;
  let MAX_ZOOM = 50;
  let MIN_CAMERA_DIST = 1;
  const DEFAULT_ZOOM = 5;
  const ISO_ROT_X = 0.38;
  const ISO_ROT_Y = -0.78;
  let rotX = ISO_ROT_X;
  let rotY = ISO_ROT_Y;
  let targetZoom = DEFAULT_ZOOM;
  let lookAtX = 0;
  let lookAtY = 0;
  let lookAtZ = 0;

  const clampZoom = (z: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));

  function updateCamera(): void {
    const dist = baseDist / targetZoom;
    camera.position.set(
      lookAtX + Math.sin(rotY) * Math.cos(rotX) * dist,
      lookAtY + Math.sin(rotX) * dist,
      lookAtZ + Math.cos(rotY) * Math.cos(rotX) * dist,
    );
    camera.lookAt(lookAtX, lookAtY, lookAtZ);
  }
  function resetLookAt(): void {
    lookAtX = lookAtY = lookAtZ = 0;
  }

  function refit(): void {
    const box = new THREE.Box3().setFromObject(group);
    if (box.isEmpty()) {
      baseDist = 10;
      MIN_ZOOM = 0.1;
      MAX_ZOOM = 50;
      MIN_CAMERA_DIST = 1;
      updateCamera();
      redraw();
      return;
    }
    let modelRadius = 0;
    for (const x of [box.min.x, box.max.x])
      for (const y of [box.min.y, box.max.y])
        for (const z of [box.min.z, box.max.z]) modelRadius = Math.max(modelRadius, Math.hypot(x, y, z));
    baseDist = (modelRadius / Math.sin(halfFovRad)) * 1.25;
    MIN_CAMERA_DIST = Math.max(modelRadius * 0.01, 0.001);
    MIN_ZOOM = baseDist / (camera.far * 0.9);
    MAX_ZOOM = baseDist / MIN_CAMERA_DIST;
    targetZoom = clampZoom(targetZoom);
    updateCamera();
    redraw();
  }

  let dimCommands: DimCommand[] = [];
  // optional: draw only some of the dimensions (e.g. only the thicknesses in the report side view)
  let dimFilter: DimFilter | null = null;

  function projectToScreen(x: number, y: number, z: number): ScreenPoint {
    const v = new THREE.Vector3(x, y, z).project(camera);
    const rect = container.getBoundingClientRect();
    return { x: (v.x * 0.5 + 0.5) * rect.width, y: (1 - (v.y * 0.5 + 0.5)) * rect.height, behind: v.z > 1 };
  }
  function tickMark45(px: number, py: number, ux: number, uy: number): void {
    const half = 5;
    const c45 = 0.7071067811865476;
    const tx = ux * c45 - uy * c45;
    const ty = ux * c45 + uy * c45;
    drawGroup.appendChild(
      svgEl("line", {
        x1: px - tx * half,
        y1: py - ty * half,
        x2: px + tx * half,
        y2: py + ty * half,
        stroke: DIM_COLOR,
        "stroke-width": 1.1,
      }),
    );
  }
  function renderDim(cmd: DimCommand): void {
    if (cmd.kind === "line") {
      const { p1, p2, offsetDir, offsetAmt, label, opts } = cmd;
      const light = opts && opts.light;
      const o1 = p1.clone().addScaledVector(offsetDir, offsetAmt);
      const o2 = p2.clone().addScaledVector(offsetDir, offsetAmt);
      const s1 = projectToScreen(p1.x, p1.y, p1.z);
      const s2 = projectToScreen(p2.x, p2.y, p2.z);
      const so1 = projectToScreen(o1.x, o1.y, o1.z);
      const so2 = projectToScreen(o2.x, o2.y, o2.z);
      if (s1.behind || s2.behind || so1.behind || so2.behind) return;
      for (const [a, b] of [
        [s1, so1],
        [s2, so2],
      ] as const) {
        drawGroup.appendChild(
          svgEl("line", {
            x1: a.x,
            y1: a.y,
            x2: b.x,
            y2: b.y,
            stroke: "#9a9a9a",
            "stroke-width": 0.75,
            "stroke-dasharray": "2,2",
            opacity: 0.9,
          }),
        );
      }
      if (light) {
        drawGroup.appendChild(
          svgEl("line", {
            x1: so1.x,
            y1: so1.y,
            x2: so2.x,
            y2: so2.y,
            stroke: DIM_COLOR,
            "stroke-width": 1,
            opacity: 0.85,
          }),
        );
        const dlen = Math.hypot(so2.x - so1.x, so2.y - so1.y) || 1;
        const ux = (so2.x - so1.x) / dlen;
        const uy = (so2.y - so1.y) / dlen;
        tickMark45(so1.x, so1.y, ux, uy);
        tickMark45(so2.x, so2.y, ux, uy);
      } else {
        drawGroup.appendChild(
          svgEl("line", {
            x1: so1.x,
            y1: so1.y,
            x2: so2.x,
            y2: so2.y,
            stroke: DIM_COLOR,
            "stroke-width": 1.2,
            "marker-start": `url(#${markerId})`,
            "marker-end": `url(#${markerId})`,
          }),
        );
      }
      const mx = (so1.x + so2.x) / 2;
      const my = (so1.y + so2.y) / 2;
      const t = svgEl("text", {
        x: mx,
        y: my - 3,
        "text-anchor": "middle",
        "font-size": 11,
        fill: DIM_COLOR,
        "font-family": DIM_FONT,
        "font-weight": "600",
        "paint-order": "stroke",
        stroke: "#fff",
        "stroke-width": 3.5,
      });
      t.textContent = label;
      drawGroup.appendChild(t);
    } else {
      const s = projectToScreen(cmd.x, cmd.y, cmd.z);
      if (s.behind) return;
      const t = svgEl("text", {
        x: s.x,
        y: s.y + 4,
        "text-anchor": "middle",
        "font-size": 12,
        fill: LABEL_COLOR,
        "font-family": DIM_FONT,
        "font-weight": "700",
        "paint-order": "stroke",
        stroke: "#fff",
        "stroke-width": 3.5,
      });
      t.textContent = cmd.text;
      drawGroup.appendChild(t);
    }
  }
  function renderOverlay(): void {
    while (drawGroup.firstChild) drawGroup.removeChild(drawGroup.firstChild);
    dimCommands
      .filter((c) => (dimFilter ? dimFilter(c) : !(c.kind === "line" && c.opts && c.opts.only)))
      .forEach(renderDim);
  }

  function redraw(): void {
    if (disposed) return;
    renderer.render(scene, camera);
    renderOverlay();
  }

  function setView(v: ViewName): void {
    if (v === "iso") {
      rotX = ISO_ROT_X;
      rotY = ISO_ROT_Y;
      targetZoom = clampZoom(DEFAULT_ZOOM);
    }
    if (v === "front") {
      rotX = 0;
      rotY = 0;
    }
    if (v === "top") {
      rotX = Math.PI / 2;
      rotY = 0;
    }
    if (v === "side") {
      rotX = 0;
      rotY = -Math.PI / 2;
    }
    resetLookAt();
    updateCamera();
  }

  function frameBoxNow(box: THREE.Box3, marginFactor: number | undefined): void {
    if (box.isEmpty()) return;
    const center = box.getCenter(new THREE.Vector3());
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const desiredDist = Math.max(
      (sphere.radius / Math.sin(halfFovRad)) * (marginFactor || 1.25),
      MIN_CAMERA_DIST * 1.5,
    );
    targetZoom = clampZoom(baseDist / desiredDist);
    lookAtX = center.x;
    lookAtY = center.y;
    lookAtZ = center.z;
    updateCamera();
  }

  sizeToContainer();
  setView("iso");

  return {
    container,
    overlay,
    renderer,
    group,
    sideFrame: null,
    clearGroup() {
      dimCommands = [];
      const removed = [...group.children];
      group.clear();
      removed.forEach(disposeObjectTree);
    },
    refit,
    resize: sizeToContainer,
    redraw,
    addDimLine(p1, p2, offsetDir, offsetAmt, label, opts) {
      dimCommands.push({ kind: "line", p1, p2, offsetDir, offsetAmt, label, opts });
    },
    addLabel(x, y, z, text) {
      dimCommands.push({ kind: "label", x, y, z, text });
    },
    setDimFilter(fn) {
      dimFilter = fn || null;
    },
    setView,
    frameGroupNow(marginFactor) {
      frameBoxNow(new THREE.Box3().setFromObject(group), marginFactor);
    },
    setOrtho(on) {
      camera.fov = on ? 2 : 45;
      camera.updateProjectionMatrix();
    },
    frameRectNow(cx, cy, cz, halfW, halfH) {
      const halfVis = Math.max(halfH, halfW / Math.max(camera.aspect || 1, 0.1));
      const D = halfVis / Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
      lookAtX = cx;
      lookAtY = cy;
      lookAtZ = cz;
      targetZoom = clampZoom(baseDist / D);
      updateCamera();
    },
    frameProductNow(marginFactor) {
      const box = new THREE.Box3();
      group.children.forEach((o) => {
        if ((o.userData as ViewerUserData).type) box.union(new THREE.Box3().setFromObject(o));
      });
      frameBoxNow(box.isEmpty() ? new THREE.Box3().setFromObject(group) : box, marginFactor || 3.2);
    },
    woodMaterial,
    dispose() {
      if (disposed) return;
      disposed = true;
      disposeObjectTree(group);
      group.clear();
      woodMaterials.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      container.innerHTML = "";
    },
  };
}
